import { z } from 'zod';
import { entityBase, id, organizationId, timestamp } from './common.js';

/** Comprehensive v1 credential set. */
export const credentialType = z.enum([
  'state_dental_license',
  'cpr_bls',
  'radiology_xray_cert',
  'hepatitis_b',
  'liability_insurance',
  'diploma',
  'other',
]);
export type CredentialType = z.infer<typeof credentialType>;

/** Types whose review cannot be completed without an uploaded supporting document. */
export const CREDENTIAL_TYPES_REQUIRING_DOCUMENT: readonly CredentialType[] = [
  'state_dental_license',
  'cpr_bls',
  'radiology_xray_cert',
  'liability_insurance',
];

/** Types that are time-bound and get expiration tracking + reminders. */
export const CREDENTIAL_TYPES_WITH_EXPIRY: readonly CredentialType[] = [
  'state_dental_license',
  'cpr_bls',
  'radiology_xray_cert',
  'liability_insurance',
];

export const credentialReviewStatus = z.enum(['pending', 'approved', 'rejected', 'expired']);
export type CredentialReviewStatus = z.infer<typeof credentialReviewStatus>;

export const credential = entityBase.extend({
  professionalId: id,
  type: credentialType,
  /** Optional human label (e.g. "TX RDH License"). */
  label: z.string().nullable(),
  number: z.string().nullable(),
  issuingAuthority: z.string().nullable(),
  issuingState: z.string().nullable(),
  issuedAt: timestamp.nullable(),
  expiresAt: timestamp.nullable(),
  /** R2 object key; the file itself is served via signed, expiring links (platform). */
  documentRef: z.string().nullable(),
  reviewStatus: credentialReviewStatus,
  reviewedByUserId: id.nullable(),
  reviewedAt: timestamp.nullable(),
  reviewNotes: z.string().nullable(),
});
export type Credential = z.infer<typeof credential>;

export const createCredentialInput = z.object({
  organizationId,
  professionalId: id,
  type: credentialType,
  label: z.string().nullish(),
  number: z.string().nullish(),
  issuingAuthority: z.string().nullish(),
  issuingState: z.string().nullish(),
  issuedAt: timestamp.nullish(),
  expiresAt: timestamp.nullish(),
  documentRef: z.string().nullish(),
});
export type CreateCredentialInput = z.infer<typeof createCredentialInput>;

export const reviewCredentialInput = z
  .object({
    reviewStatus: z.enum(['approved', 'rejected']),
    reviewedByUserId: id,
    reviewNotes: z.string().trim().max(2000).nullish(),
  })
  .refine((value) => value.reviewStatus !== 'rejected' || Boolean(value.reviewNotes), {
    path: ['reviewNotes'],
    message: 'Tell the professional what needs to change',
  });
export type ReviewCredentialInput = z.infer<typeof reviewCredentialInput>;
